import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "../scoped-merchant-client";

export const useVideoPlayback = (id: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["video-playback", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video[":videoId"].playback.$get({
            param: { ...scope.param, videoId: id },
          })
        : await client.merchant[":merchantId"].assurance.video[":videoId"].playback.$get({
            param: { ...scope.param, videoId: id },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    // Play URLs are signed with an expiry a few hours out, so they are refetched rather than cached long.
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    enabled: !!scope.merchantId && !!id,
  });
};

export const useAssuranceVideo = (id: string, opts?: { includeUpcomingPolicySteps?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["assurance-video", scope.merchantId, scope.workspaceId, id, opts?.includeUpcomingPolicySteps],
    queryFn: async () => {
      const query = { upcoming: opts?.includeUpcomingPolicySteps ? "true" : undefined };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video[":videoId"].$get({
            param: { ...scope.param, videoId: id },
            query,
          })
        : await client.merchant[":merchantId"].assurance.video[":videoId"].$get({
            param: { ...scope.param, videoId: id },
            query,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!id,
  });
};

const invalidateManualReview = (queryClient: ReturnType<typeof useQueryClient>, merchantId: string | undefined) => {
  queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["video", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["video-overview", merchantId] });
};

export const useConfirmNoRisks = (videoId: string) => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video[":videoId"][
            "manual-review"
          ].$post({
            param: { ...scope.param, videoId },
          })
        : await client.merchant[":merchantId"].assurance.video[":videoId"]["manual-review"].$post({
            param: { ...scope.param, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSettled: () => invalidateManualReview(queryClient, scope.merchantId),
  });
};

export const useUndoNoRisksConfirmation = (videoId: string) => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video[":videoId"][
            "manual-review"
          ].$delete({
            param: { ...scope.param, videoId },
          })
        : await client.merchant[":merchantId"].assurance.video[":videoId"]["manual-review"].$delete({
            param: { ...scope.param, videoId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSettled: () => invalidateManualReview(queryClient, scope.merchantId),
  });
};
